/**
 * LLM Tier Service (AdminClient / cabinet portal)
 * ---------------------------------------------------------------------------
 * Mirror of the client-portal service. The user picks an LLM *tier* (among the
 * tiers the platform admin enabled) and — when the admin allows it for that
 * tier — a specific model within it. The choice is stored per-user in
 * billing-service and read by the ai-orchestrator at call time (all inference
 * egresses through the LiteLLM gateway; tools/skills/prompts via MCP gateway).
 *
 *   GET  /api/v1/credits/llm-tiers
 *   GET  /api/v1/credits/llm-preference/{tenant}/{user}
 *   PUT  /api/v1/credits/llm-preference/{tenant}/{user}  { tier, model_code }
 *
 * Paths are relative to the API base (the `/api/v1` prefix is added by the axios
 * baseURL in cloud-gateway mode, or by resolveApiUrl in cloud-direct mode).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { get, put } from '@/lib/api/client'
import { getTenantId, getUserId } from '@/lib/auth/keycloak'

export interface LlmTierModel {
  code: string
  provider: string
  display_name: string
  input_per_million: number
  output_per_million: number
}
export interface LlmTier {
  code: string
  display_name: string
  description?: string
  default_model_code?: string
  user_can_select_model: boolean
  models: LlmTierModel[]
}
export interface LlmPreference {
  tier: string
  model_code: string | null
}

export const llmTierKeys = {
  tiers: ['llm-tiers'] as const,
  preference: ['llm-preference'] as const,
}

export function useLlmTiers() {
  return useQuery({
    queryKey: llmTierKeys.tiers,
    queryFn: async () => {
      const res = await get<{ tiers: LlmTier[] }>('/credits/llm-tiers')
      return res?.tiers ?? []
    },
    staleTime: 5 * 60_000,
    retry: false,
  })
}

export function useLlmPreference() {
  return useQuery({
    queryKey: llmTierKeys.preference,
    queryFn: () => get<LlmPreference>(`/credits/llm-preference/${getTenantId()}/${getUserId()}`),
    staleTime: 60_000,
    retry: false,
  })
}

export function useSetLlmPreference() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { tier: string; model_code?: string | null }) =>
      put<LlmPreference>(`/credits/llm-preference/${getTenantId()}/${getUserId()}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: llmTierKeys.preference }),
  })
}
